import {formatDate, formatTime, secondsToHms} from '../utils';
import defaultChannelImage from '@src/assets/images/content/channel/128.jpg';
import defaultVideoImage from '@src/assets/images/content/video/100x150.jpg';
import defaultShowImage from '@src/assets/images/content/video/100x75.jpg';
import defaultItemImage from '@src/assets/images/content/items/244x144.jpg';
import {AppsProps, ContentType, Resource, Uid, EpgUpdateStatus, TrackStatus, ContentDisplayMode} from 'sdk/src/defines';
import AbstractResource from '../abstract/abstract-resource';
import {TFunction} from 'react-i18next';
import AbstractFileModelResource from '../abstract/abstract-file-model-resource';
import tags from '../tags';
import {
    AttributesType,
    CastRule,
    CastType,
    DetailsType,
    DispatchFunc,
    DropDownItem, ErrorType,
    LabelsType, LocalesType,
} from '../../utility/types';
import i18n from '../../i18n';

export type EpgParams = {
    id: string,
    url: string,
    timeShift?: number,
    duration?: number,
    updatedAt?: number,
    pluginInstanceId?: string,
    status?: EpgUpdateStatus,
    lastEpgEndAt?: number | string,
    checkedAt?: number,
};

export type EpgLastProgramStatus = {
    value: string,
    hasError: boolean,
    hint?: string,
};

class Content extends AbstractFileModelResource {
    public resourceType: Resource = Resource.CONTENTS;

    public labels: LabelsType = {
        id: 'ID',
        title: 'contents.title',
        type: 'contents.type',
        url: 'contents.url',
        duration: 'contents.duration',
        sizeInBytes: 'contents.fileSize',
        folderId: 'contents.folderID',
        beginAt: 'contents.availableFrom',
        endAt: 'contents.availableTo',
    };

    public declare beginAt: Date;

    public declare endAt: Date;

    public declare displayMode: ContentDisplayMode;

    public declare externalId: string;

    public declare contentOwnerId: Uid;

    public declare geoIpId: Uid;

    public declare sharedFromContentId: Uid;

    public declare parentContentId: Uid;

    public declare type: ContentType;

    public declare title: string;

    public declare duration: number;

    public declare rating: number;

    public declare ageLimit: number;

    public declare availableWithoutPurchase: boolean;

    public declare showToDemoUsers: boolean;

    public declare showIfNotPurchased: boolean;

    public declare price: number;

    public declare locales: LocalesType;

    public declare props: {
        url?: string,
        fullDescription?: string,
        shortDescription?: string,
        keyCode?: number,
        year?: number,
        month?: number,
        day?: number,
        synopsis?: string,
        app?: string,
        apps?: AppsProps,
        imdbRating?: number,
        index?: number,
        languages?: string[],
        epg?: EpgParams,
    };

    public declare storage: {
        epgUpdatedAt?: number | string,
        epgStatus?: EpgUpdateStatus,
        lastEpgEndAt?: number | string,
        epgCheckedAt?: number | string,
    };

    public declare categories: {categoryId: Uid}[];

    public declare packages: {packageId: Uid}[];

    public declare discounts: {discountId: Uid}[];

    public declare groups: {groupId: Uid}[];

    public declare shared: {operatorId: Uid}[];

    public declare tags: {tagId: Uid}[];

    public declare tracks: {pluginInstanceId: Uid, props: AttributesType, published: boolean, status: TrackStatus}[];

    public castValues(): CastType {
        return {
            beginAt: CastRule.DATE,
            endAt: CastRule.DATE,
            duration: CastRule.FLOAT,
            rating: CastRule.FLOAT,
            ageLimit: CastRule.INTEGER,
            price: CastRule.FLOAT,
        };
    }

    public static hasPackages(type: ContentType): boolean {
        return [
            ContentType.CHANNEL,
            ContentType.SERIES,
            ContentType.SHOW,
            ContentType.APP,
            ContentType.VIDEO,
        ].includes(type);
    }

    public static hasGroups(type: ContentType): boolean {
        return [
            ContentType.CHANNEL,
            ContentType.SERIES,
            ContentType.SHOW,
            ContentType.APP,
            ContentType.VIDEO,
        ].includes(type);
    }

    public static hasContentOwner(type: ContentType): boolean {
        return [
            ContentType.CHANNEL,
            ContentType.SERIES,
            ContentType.SHOW,
            ContentType.VIDEO,
        ].includes(type);
    }

    public static hasSubOperators(type: ContentType): boolean {
        return [
            ContentType.CHANNEL,
            ContentType.SERIES,
            ContentType.SHOW,
            ContentType.VIDEO,
        ].includes(type);
    }

    public static isVodType(type: ContentType): boolean {
        return [
            ContentType.SERIES,
            ContentType.SHOW,
            ContentType.VIDEO,
        ].includes(type);
    }

    public static videoTypesForDropDown(t: TFunction): DropDownItem[] {
        return Content.typesForDropDown(t).filter((item: DropDownItem)=> {
            return Content.isVodType(item.value as ContentType);
        });
    }

    public getImage(name: string = 'image'): string {
        return super.getImage(Content.getDefaultImage(this.type), name);
    }

    public get published(): boolean {
        return this.displayMode !== ContentDisplayMode.NONE;
    }

    public applyMetadata(params: AttributesType) {
        return (dispatch: DispatchFunc): void => {
            dispatch({type: `${this.resourceType}_UPDATING`, id: this.id});
            this.api().get(`${Resource.CONTENTS}/metadata/apply/${this.id}`, params).then((data: AttributesType) => {
                dispatch(tags.refresh());
                dispatch(this.updated(data));
            }).catch((err: ErrorType) => {
                dispatch({type: `${this.resourceType}_UPDATE_FAILED`, id: this.id, error: err});
            });
        };
    }

    public static getImageSize(type: ContentType): {width: number, height: number} {
        switch (type) {
            case ContentType.CHANNEL:
            case ContentType.APP:
                return {width: 400, height: 400};
            case ContentType.VIDEO:
            case ContentType.SERIES:
            case ContentType.SHOW:
                return {width: 1200, height: 1772};
            case ContentType.ITEM:
                return {width: 1600, height: 1015};
            case ContentType.NEWS:
                return {width: 400, height: 300};
            default:
                return {width: 400, height: 400};
        }
    }

    public static getDefaultImage(type: ContentType): string {
        switch (type) {
            case ContentType.CHANNEL:
                return defaultChannelImage;
            case ContentType.VIDEO:
            case ContentType.SERIES:
            case ContentType.SEASON:
            case ContentType.EPISODE:
                return defaultVideoImage;
            case ContentType.SHOW:
                return defaultShowImage;
            case ContentType.ITEM:
                return defaultItemImage;
            case ContentType.NEWS:
            case ContentType.APP:
                return defaultShowImage;
            default:
                return null;
        }
    }

    public static ageLimitsForDropDown(): DropDownItem[] {
        return [
            {label: '0+', value: 0},
            {label: '18+', value: 18},
        ];
    }

    public getDetails(t: TFunction): DetailsType {
        return super.getDetails(t, {
            title: this.getTitle(t),
            beginAt: !this.beginAt ? '' : `${formatDate(this.beginAt)} ${formatTime(this.beginAt)}`,
            endAt: !this.endAt ? '' : `${formatDate(this.endAt)} ${formatTime(this.endAt)}`,
            duration: this.duration ? secondsToHms(this.duration) : '',
        });
    }

    public getDurationMs(): number {
        if (this.duration) {
            return this.duration * 1000;
        }

        return 60000;
    }

    public getTitle(t: TFunction): string {
        return this.title || t('contents.unnamedContent');
    }

    public static typesForDropDown(t: TFunction): DropDownItem[] {
        return AbstractResource.defineForDropDown(ContentType, 'contents', t);
    }

    public getTypeStr(t: TFunction): string {
        const item: DropDownItem = Content
            .typesForDropDown(t)
            .find((item: DropDownItem) => item.value === this.type);

        if (item) {
            return item.label;
        }

        return null;
    }

    public getEpgLastUpdateStatus(t: TFunction): string {
        const {epgUpdatedAt, epgStatus, lastEpgEndAt} = this.storage;

        let dateString: string = '';

        if (epgUpdatedAt) {
            const date: Date = new Date(epgUpdatedAt);
            const time: Date = new Date(epgUpdatedAt);

            dateString = formatDate(date, i18n.language) + ' ' + formatTime(time, i18n.language);
        }

        switch (epgStatus) {
            case EpgUpdateStatus.UPDATED:
                if (new Date(lastEpgEndAt).getTime() <= Date.now()) {
                    return dateString + ' ' + `(${t('contents.epgEnded')})`;
                } else {
                    if (epgUpdatedAt) {
                        return dateString;
                    }
                    break;
                }
            case EpgUpdateStatus.ERROR:
                return dateString + ' ' + `(${t('contents.epgUpdateError')})`;
        }

        return t('never');
    }

    public getEpgLastCheckStatus(t: TFunction): string {
        const epgCheckedAt: number | string = this.storage.epgCheckedAt;

        if (epgCheckedAt) {
            const date: Date = new Date(epgCheckedAt);
            const time: Date = new Date(epgCheckedAt);
    
            return formatDate(date, i18n.language) + ' ' + formatTime(time, i18n.language);
        } else {
            return t('never');
        }
    }

    public getEpgLastProgramStatus(t: TFunction): EpgLastProgramStatus {
        const {epgCheckedAt, epgStatus, lastEpgEndAt} = this.storage;
        const isEpgConfigured: boolean = Boolean(this.props.epg?.url && this.props.epg?.id);
        const lastProgramAt: Date = lastEpgEndAt ? new Date(lastEpgEndAt) : null;
        const hasLastProgram: boolean = Boolean(lastProgramAt && !Number.isNaN(lastProgramAt.getTime()));
        const value: string = hasLastProgram
            ? `${formatDate(lastProgramAt, i18n.language)} ${formatTime(lastProgramAt, i18n.language)}`
            : t('never');
        const checkedAt: string = epgCheckedAt ? this.getEpgLastCheckStatus(t) : t('never');

        if (epgStatus === EpgUpdateStatus.ERROR) {
            return {
                value,
                hasError: true,
                hint: t('contents.epgUpdateErrorHint', {checkedAt}),
            };
        }

        if (isEpgConfigured && !hasLastProgram) {
            return {
                value,
                hasError: true,
                hint: t('contents.epgNoProgramsHint', {checkedAt}),
            };
        }

        if (hasLastProgram && lastProgramAt.getTime() <= Date.now()) {
            return {
                value,
                hasError: true,
                hint: t('contents.epgEndedHint', {lastProgramAt: value, checkedAt}),
            };
        }

        return {value, hasError: false};
    }
}

export default Content;
